import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react'
import resizeIcon from '../../../assets/images/icons/collapse.svg'
import { SamplesContext, UpdateSamplesContext } from '../../../context/samplesContext'
import { formatNumber } from '../../utils/helper'
import { useTableContext } from '../../../context/tableContext'
import { Modal } from '../../../components/Modal'
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable'
import { getResolutionFromSample } from '../../utils/resolution'
import { drawMeasurementPath } from '../../utils/drawMeasurement'
import debounce from 'lodash/debounce'
import { SelectedImageInfo } from '../types'
import ReactLoading from 'react-loading'
import { loadPreviewImage } from '../../utils/loadPreviewImage'
import { useTranslation } from 'react-i18next'

type Point = { x: number; y: number }
type MeasurementData = {
  measure_index: number
  measure_name: string
  image_index: number
  resolution: number
  result_pixel: number
  result_real: number
  result_point: Point[]
  measure_type: 'auto' | 'manual' | ''
  is_deleted: boolean
  time: number
}

type CanvasImageSize = {
  img: HTMLImageElement
  w: number
  h: number
  dpr: number
}

// リサイズ監視のデバウンス間隔（ミリ秒）
const RESIZE_DEBOUNCE_MS = 150
// 頂点マーカー（三角形）のサイズ関連
const TRIANGLE_HALF_WIDTH = 6
const TRIANGLE_HEIGHT = 8
// 頂点マーカー（三角形）のボーダー
const TRIANGLE_OUTLINE_OFFSET = 2
const TRIANGLE_OUTLINE_EXTRA_WIDTH = 2
const TRIANGLE_OUTLINE_EXTRA_HEIGHT = 3
// 頂点マーカー（三角形）の縁取り色
const TRIANGLE_OUTLINE_COLOR = '#fff'
// 頂点マーカー（三角形）内側の上方向オフセット
const TRIANGLE_INNER_TOP_OFFSET = 1
// ズームの最小・最大・変化量
const MIN_ZOOM = 0.5
const MAX_ZOOM = 3
const ZOOM_STEP = 0.1
// マウス中ボタンの識別値（MouseEvent.button）
const MIDDLE_MOUSE_BUTTON = 1

export function ImageContainer({
  setManualDrawingIndex,
  manualDrawingIndex,
  selectedImage,
}: {
  setManualDrawingIndex: React.Dispatch<React.SetStateAction<number | null>>
  manualDrawingIndex: number | null
  selectedImage: SelectedImageInfo | null
}) {
  const { t } = useTranslation()
  const { rowSample } = useContext(SamplesContext)
  const { updateRowSample, updateCanvasImage } = useContext(UpdateSamplesContext)
  const { getColor, getDisplayIndex, hasColor } = useTableContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0
  })
  const [clickedPoints, setClickedPoints] = useState<Point[]>([])

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const pendingManualRef = useRef<MeasurementData | null>(null)
  const canvasWrapperRef = useRef<HTMLDivElement>(null)

  const selectedImageUrl = selectedImage?.image.url || ''
  const selectedImageIndex = selectedImage?.image.image_index
  const isSampleImageSelected = selectedImage?.source === 'sample'
  const hasImage = !!selectedImageUrl

  const [previewPoint, setPreviewPoint] = useState<Point | null>(null)

  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [draggingPos, setDraggingPos] = useState<Point>({ x: 0, y: 0 })
  const [canvasSize, setCanvasSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 })
  const [interactionMode, setInteractionMode] = useState<'draw' | 'move'>('draw')
  const [zoom, setZoom] = useState<number>(1)
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 })
  const [isSpacePressed, setIsSpacePressed] = useState(false)
  const [isPanning, setIsPanning] = useState(false)
  const [isMiddleMousePressed, setIsMiddleMousePressed] = useState(false)
  const panStartRef = useRef<Point>({ x: 0, y: 0 })
  const panOriginRef = useRef<Point>({ x: 0, y: 0 })
  const resetZoom = useCallback(() => {
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }, [])

  const nodeRef = useRef<HTMLDivElement>(null)

  const [resolution, setResolution] = useState<number>(0)
  const [isCanvasReady, setIsCanvasReady] = useState(false)
  const [isBaseImageLoading, setIsBaseImageLoading] = useState(false)
  const [baseImage, setBaseImage] = useState<HTMLImageElement | null>(null)
  const isMac = useMemo(() => typeof window !== 'undefined' && /Mac/i.test(window.navigator.userAgent), [])
  const isDraggingPoint = draggingIndex !== null

  useEffect(() => {
    if (!rowSample?.id || rowSample?.id === '') return // 未ロード時
    try {
      const r = getResolutionFromSample(rowSample)
      setResolution(r)
    } catch (_e) {
      console.error('Resolution determination failed:', _e)
      alert(t('messages.resolutionUnknown'))
      setResolution(0)
    }
  }, [rowSample?.id, rowSample?.sampleMeasurements, t])

  useEffect(() => {
    setPreviewPoint(null)
  }, [manualDrawingIndex, selectedImageIndex])

  const isColorReady = useMemo(() => {
    if (!isSampleImageSelected || typeof selectedImageIndex !== 'number') return true
    const relevantMeasurements = rowSample.sampleMeasurements.filter(m => {
      if (m.image_index !== selectedImageIndex) return false
      if (m.is_deleted) return false
      return true
    })
    if (relevantMeasurements.length === 0) return true
    return relevantMeasurements.every(m => hasColor(m.measure_index))
  }, [isSampleImageSelected, selectedImageIndex, rowSample.sampleMeasurements, hasColor])

  useEffect(() => {
    if (hasImage) {
      setIsCanvasReady(false)
    } else if (!hasImage) {
      setIsCanvasReady(true)
    }
  }, [hasImage, selectedImageUrl, manualDrawingIndex, rowSample.sampleMeasurements, selectedImageIndex])

  useEffect(() => {
    if (!hasImage || !selectedImageUrl) {
      setBaseImage(null)
      setIsBaseImageLoading(false)
      return
    }
    let canceled = false
    setIsBaseImageLoading(true)
    loadPreviewImage(selectedImageUrl, { crossOrigin: 'anonymous' })
      .then(img => {
        if (canceled) return
        setBaseImage(img)
      })
      .catch(() => {
        if (canceled) return
        setBaseImage(null)
      })
      .finally(() => {
        if (!canceled) {
          setIsBaseImageLoading(false)
        }
      })
    return () => {
      canceled = true
    }
  }, [selectedImageUrl, hasImage])

  useEffect(() => {
    setInteractionMode('draw')
  }, [manualDrawingIndex])

  useEffect(() => {
    if (interactionMode === 'move') {
      setPreviewPoint(null)
    }
  }, [interactionMode])

  const clampZoom = useCallback((value: number) => {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, parseFloat(value.toFixed(2))))
  }, [])

  const applyZoomDelta = useCallback((deltaY: number) => {
    const direction = deltaY < 0 ? 1 : -1
    setZoom(prev => clampZoom(prev + direction * ZOOM_STEP))
  }, [clampZoom])

  const getCanvasRelativePoint = useCallback((clientX: number, clientY: number) => {
    if (!canvasRef.current) return null
    const rect = canvasRef.current.getBoundingClientRect()
    const scale = zoom || 1
    return {
      x: (clientX - rect.left) / scale,
      y: (clientY - rect.top) / scale
    }
  }, [zoom])

  useEffect(() => {
    const wrapper = canvasWrapperRef.current
    if (!wrapper) return
    const handleWheel = (event: WheelEvent) => {
      const modifierPressed = isMac ? event.altKey : event.ctrlKey
      if (
        !modifierPressed ||
        manualDrawingIndex === null ||
        !canvasRef.current ||
        !canvasRef.current.contains(event.target as Node)
      ) {
        return
      }
      event.preventDefault()
      applyZoomDelta(event.deltaY)
    }
    wrapper.addEventListener('wheel', handleWheel, { passive: false })
    return () => {
      wrapper.removeEventListener('wheel', handleWheel)
    }
  }, [applyZoomDelta, manualDrawingIndex])

  useEffect(() => {
    if (manualDrawingIndex === null) {
      setIsSpacePressed(false)
      setIsPanning(false)
    }
  }, [manualDrawingIndex])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || zoom === 1 || manualDrawingIndex === null) return
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }
      if (!isSpacePressed) {
        event.preventDefault()
        setIsSpacePressed(true)
      }
    }
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.code !== 'Space') return
      setIsSpacePressed(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [isSpacePressed, zoom, manualDrawingIndex])

  useEffect(() => {
    if (zoom === 1 && (pan.x !== 0 || pan.y !== 0)) {
      setPan({ x: 0, y: 0 })
    }
  }, [zoom, pan])

  useEffect(() => {
    if (!isSpacePressed && !isMiddleMousePressed && isPanning) {
      setIsPanning(false)
    }
  }, [isSpacePressed, isMiddleMousePressed, isPanning])

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isPanning) return
      const dx = event.clientX - panStartRef.current.x
      const dy = event.clientY - panStartRef.current.y
      setPan({
        x: panOriginRef.current.x + dx,
        y: panOriginRef.current.y + dy,
      })
    }
    const handleMouseUp = (event: MouseEvent) => {
      if (event.button === MIDDLE_MOUSE_BUTTON) {
        setIsMiddleMousePressed(false)
      }
      if (isPanning) {
        setIsPanning(false)
      }
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isPanning])

  const handlePanMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (zoom === 1 || manualDrawingIndex === null) return
    const isMiddleMouse = event.button === MIDDLE_MOUSE_BUTTON
    if (!isSpacePressed && !isMiddleMouse) return
    event.preventDefault()
    if (isMiddleMouse) {
      setIsMiddleMousePressed(true)
    }
    panStartRef.current = { x: event.clientX, y: event.clientY }
    panOriginRef.current = pan
    setIsPanning(true)
  }, [isSpacePressed, zoom, pan, manualDrawingIndex])

  const getTouchCenter = useCallback((touches: TouchList | React.TouchList) => {
    const points: Point[] = []
    for (let i = 0; i < touches.length; i++) {
      const touch = touches.item(i)
      if (!touch) continue
      points.push({ x: touch.clientX, y: touch.clientY })
    }
    if (!points.length) return { x: 0, y: 0 }
    const sum = points.reduce(
      (acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }),
      { x: 0, y: 0 }
    )
    return { x: sum.x / points.length, y: sum.y / points.length }
  }, [])

  const handlePanTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (zoom === 1 || event.touches.length < 2) return
    event.preventDefault()
    const center = getTouchCenter(event.touches)
    panStartRef.current = center
    panOriginRef.current = pan
    setIsPanning(true)
  }, [zoom, getTouchCenter, pan])

  const handlePanTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (!isPanning || event.touches.length < 2) return
    event.preventDefault()
    const center = getTouchCenter(event.touches)
    const dx = center.x - panStartRef.current.x
    const dy = center.y - panStartRef.current.y
    setPan({
      x: panOriginRef.current.x + dx,
      y: panOriginRef.current.y + dy,
    })
  }, [isPanning, getTouchCenter])

  const handlePanTouchEnd = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length >= 2) return
    setIsPanning(false)
  }, [])

  // ── リサイズ監視 ──
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const applySize = (width: number, height: number) => {
      setContainerSize(prev =>
        prev.width !== width || prev.height !== height ? { width, height } : prev
      )
    }
    applySize(el.clientWidth, el.clientHeight)
    const debouncedUpdateSize = debounce((width: number, height: number) => {
      applySize(width, height)
    }, RESIZE_DEBOUNCE_MS)
    const ro = new ResizeObserver(entries => {
      if (!entries.length) return
      const { width, height } = entries[0].contentRect
      const w = Math.floor(width)
      const h = Math.floor(height)
      debouncedUpdateSize(w, h)
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
      debouncedUpdateSize.cancel()
    }
  }, [])

  // ── ユーティリティ ──
  const getCanvasSizes = useCallback((): CanvasImageSize | null => {
    if (!hasImage || !containerSize.width || !containerSize.height || !baseImage) return null
    const img = baseImage
    const widthRatio = containerSize.width / img.width
    const heightRatio = containerSize.height / img.height
    const scale = Math.min(widthRatio, heightRatio)
    const w = Math.floor(img.width * scale)
    const h = Math.floor(img.height * scale)
    const dpr = window.devicePixelRatio || 1
    return { img, w, h, dpr }
  }, [hasImage, containerSize, baseImage])

  // 既存manual編集時に各座標復元
  useEffect(() => {
    if (
      manualDrawingIndex === null ||
      !isSampleImageSelected ||
      typeof selectedImageIndex !== 'number'
    ) {
      setClickedPoints([])
      return
    }
    const existing = rowSample.sampleMeasurements.find(
      m =>
        m.measure_type === 'manual' &&
        m.measure_index === manualDrawingIndex &&
        m.image_index === selectedImageIndex
    )
    if (existing) {
      const sizes = getCanvasSizes()
      if (!sizes) return
      const { img, w, h } = sizes
      const scaleX = w / img.width
      const scaleY = h / img.height
      const pts = existing.result_point.map(p => ({
        x: p.x * scaleX,
        y: p.y * scaleY,
      }))
      setClickedPoints(pts)
    } else {
      setClickedPoints([])
    }
  }, [manualDrawingIndex, rowSample.sampleMeasurements, getCanvasSizes, selectedImageIndex, isSampleImageSelected])

  const calcTotalPx = (pts: Point[]) =>
    pts.slice(1).reduce((sum, p, i) => {
      const prev = pts[i]
      return sum + Math.hypot(p.x - prev.x, p.y - prev.y)
    }, 0)

  const applyManualRecalc = useCallback(
    (imgSizes: CanvasImageSize | null) => {
      if (!imgSizes) return
      if (!isSampleImageSelected || typeof selectedImageIndex !== 'number') {
        return
      }
      // manual の result_real を再計算して必要なら更新
      const updated = rowSample.sampleMeasurements.map(m => {
        if (
          m.measure_type !== 'manual' ||
          m.result_point.length < 2 ||
          m.image_index !== selectedImageIndex
        ) {
          return m
        }
        const px = calcTotalPx(m.result_point)
        const newReal = parseFloat(((px * (resolution || m.resolution)) / 10).toFixed(1))
        return newReal !== m.result_real
          ? { ...m, result_pixel: px, result_real: newReal }
          : m
      })
      const changed = updated.some(
        (m, i) => m.measure_type === 'manual' && m.result_real !== rowSample.sampleMeasurements[i].result_real
      )
      if (changed) {
        updateRowSample({ sampleMeasurements: updated })
      }
    },
    [rowSample, updateRowSample, resolution, isSampleImageSelected, selectedImageIndex],
  )

  // ── 描画＆再計算エフェクト ──
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !containerSize.width || !containerSize.height) return

    const sizes = getCanvasSizes()
    const ctx = canvas.getContext('2d')

    updateCanvasImage('')

    if (!sizes || !ctx) {
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
      setIsCanvasReady(!hasImage)
      return
    }

    const { img, w, h, dpr } = sizes
    // canvas セットアップ
    canvas.width = w * dpr
    canvas.height = h * dpr
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    ctx.resetTransform()
    ctx.scale(dpr, dpr)
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    setCanvasSize({ w, h })

    // 背景描画
    ctx.clearRect(0, 0, w, h)
    ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, w, h)

    const scaleX = w / img.width
    const scaleY = h / img.height

    const activeMeasurements = rowSample.sampleMeasurements
      .filter(m => {
        if (!isSampleImageSelected || typeof selectedImageIndex !== 'number') return false
        if (m.is_deleted) return false
        if (!hasColor(m.measure_index)) return false
        if (
          manualDrawingIndex !== null &&
          m.measure_index === manualDrawingIndex &&
          m.image_index === selectedImageIndex
        ) {
          return false
        }
        return m.image_index === selectedImageIndex
      })
      .sort((a, b) => a.measure_index - b.measure_index)
      .map((m, idx) => ({ ...m, displayIndex: idx + 1 }))

    // 保存済みの線を描画
    activeMeasurements.forEach(m => {
      if (m.result_point.length < 2) return
      const px = m.result_point.map(p => ({
        x: p.x * scaleX,
        y: p.y * scaleY,
      }))
      const displayIdx = getDisplayIndex(m.measure_index)
      const color = getColor(m.measure_index)

      drawMeasurementPath({
        ctx,
        points: px,
        label: formatNumber(displayIdx),
        color,
        lineWidth: 2
      })
    })

    // 新規 manual 描画
    if (manualDrawingIndex !== null && clickedPoints.length >= 2) {
      const px = clickedPoints
      const displayIdx = getDisplayIndex(manualDrawingIndex)
      const color = getColor(manualDrawingIndex)

      drawMeasurementPath({
        ctx,
        points: px,
        label: formatNumber(displayIdx),
        color,
        lineWidth: 2
      })
    }

    if (
      manualDrawingIndex !== null &&
      clickedPoints.length >= 1 &&
      previewPoint &&
      draggingIndex === null &&
      interactionMode === 'draw'
    ) {
      const start = clickedPoints[clickedPoints.length - 1]
      const color = getColor(manualDrawingIndex)

      ctx.setLineDash([5, 5]) // 点線スタイル
      ctx.strokeStyle = color
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(start.x, start.y)
      ctx.lineTo(previewPoint.x, previewPoint.y)
      ctx.stroke()
      ctx.setLineDash([]) // 実線に戻す
    }

    // 再計算
    applyManualRecalc({ img, w, h, dpr })

    const imageDataUrl = canvas.toDataURL('image/png')
    updateCanvasImage(imageDataUrl)
    setIsCanvasReady(true)
  }, [
    rowSample.sampleImages,
    rowSample.sampleEcImages,
    rowSample.sampleMeasurements,
    containerSize,
    manualDrawingIndex,
    clickedPoints,
    previewPoint,
    draggingIndex,
    draggingPos,
    interactionMode,
    getCanvasSizes,
    applyManualRecalc,
    getColor,
    hasColor,
    getDisplayIndex,
    updateCanvasImage,
    selectedImage,
    isColorReady,
    hasImage,
  ])

  const applyManualUpdate = useCallback((manual: MeasurementData) => {
    const existing = rowSample.sampleMeasurements.find(
      m =>
        m.measure_type === 'manual' &&
        m.measure_index === manual.measure_index &&
        m.image_index === manual.image_index
    )
    const nameToUse = existing?.measure_name && existing.measure_name !== ''
      ? existing.measure_name
      : manual.measure_name

    const toSave = { ...manual, measure_name: nameToUse }

    const idx = rowSample.sampleMeasurements.findIndex(
      m =>
        m.measure_index === toSave.measure_index &&
        m.image_index === toSave.image_index &&
        m.measure_type === 'manual'
    )
    const updated =
      idx >= 0
        ? rowSample.sampleMeasurements.map(m =>
          m.measure_index === toSave.measure_index &&
            m.image_index === toSave.image_index &&
            m.measure_type === 'manual'
            ? toSave
            : m
        )
        : [...rowSample.sampleMeasurements, toSave]

    updateRowSample({ sampleMeasurements: updated })
    setManualDrawingIndex(null)
    setClickedPoints([])
  }, [rowSample, setManualDrawingIndex, updateRowSample])

  // ── クリック／完了／キャンセル ──
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (
        manualDrawingIndex === null ||
        interactionMode !== 'draw' ||
        isSpacePressed ||
        isPanning ||
        !isSampleImageSelected
      ) {
        return
      }
      const relative = getCanvasRelativePoint(e.clientX, e.clientY)
      if (!relative) return
      setClickedPoints(prev => [...prev, relative])
    },
    [manualDrawingIndex, interactionMode, getCanvasRelativePoint, isSpacePressed, isPanning, isSampleImageSelected],
  )

  const handleComplete = useCallback(() => {
    if (manualDrawingIndex === null || clickedPoints.length < 2) return

    const sizes = getCanvasSizes()
    if (!sizes) return
    const { img, w, h } = sizes
    const scaleX = img.width / w
    const scaleY = img.height / h
    const naturalPts = clickedPoints.map(p => ({
      x: p.x * scaleX,
      y: p.y * scaleY,
    }))

    const pxTotal = calcTotalPx(naturalPts)
    const targetImageIndex = isSampleImageSelected && typeof selectedImageIndex === 'number'
      ? selectedImageIndex
      : null

    if (targetImageIndex === null) {
      alert(t('messages.selectMeasurementImageForManual'))
      return
    }

    const resolution_ = resolution
    if (!Number.isFinite(resolution_) || resolution_ <= 0) {
      alert(t('messages.invalidResolution'))
      return
    }

    let manual: MeasurementData = {
      measure_index: manualDrawingIndex,
      measure_name: '',
      image_index: targetImageIndex,
      resolution: resolution_, // FIXME: 暫定対応
      result_pixel: pxTotal,
      result_real: parseFloat(((pxTotal * resolution_) / 10).toFixed(1)),
      result_point: naturalPts,
      measure_type: 'manual',
      is_deleted: false,
      time: rowSample.time
    }

    const existingManual = rowSample.sampleMeasurements.find(
      m =>
        m.measure_type === 'manual' &&
        m.measure_index === manualDrawingIndex &&
        m.image_index === targetImageIndex &&
        m.measure_name !== ''
    )
    if (existingManual) manual = { ...manual, measure_name: existingManual.measure_name }

    if (existingManual) {
      applyManualUpdate(manual)
      resetZoom()
      return
    }

    applyManualUpdate(manual)
    resetZoom()
  }, [
    clickedPoints,
    manualDrawingIndex,
    rowSample,
    resolution,
    getCanvasSizes,
    applyManualUpdate,
    resetZoom,
    selectedImage,
    selectedImageIndex,
    isSampleImageSelected,
    t,
  ])

  const handleCancel = useCallback(() => {
    if (
      manualDrawingIndex !== null &&
      isSampleImageSelected &&
      typeof selectedImageIndex === 'number'
    ) {
      const updatedMeasurements = rowSample.sampleMeasurements.filter(m => {
        const isPendingManual =
          m.measure_type === 'manual' &&
          m.measure_index === manualDrawingIndex &&
          m.image_index === selectedImageIndex &&
          m.result_point.length === 0 &&
          m.result_pixel === 0 &&
          m.result_real === 0

        return !isPendingManual
      })

      if (updatedMeasurements.length !== rowSample.sampleMeasurements.length) {
        updateRowSample({ sampleMeasurements: updatedMeasurements })
      }
    }

    setClickedPoints([])
    setDraggingPos({ x: 0, y: 0 })
    setDraggingIndex(null)
    setManualDrawingIndex(null)
    setPreviewPoint(null)
    resetZoom()
  }, [
    manualDrawingIndex,
    isSampleImageSelected,
    selectedImageIndex,
    rowSample.sampleMeasurements,
    updateRowSample,
    setManualDrawingIndex,
    resetZoom,
  ])

  const onClickModalOk = useCallback(() => {
    const manual = pendingManualRef.current!
    const existing = rowSample.sampleMeasurements.find(
      m =>
        m.measure_type === 'manual' &&
        m.measure_name === '' &&
        m.measure_index !== manualDrawingIndex &&
        m.image_index === manual.image_index
    )
    let updated = rowSample.sampleMeasurements.filter(m => m !== existing)
    updated.push({
      ...manual,
      measure_index: existing?.measure_index as number
    })

    updateRowSample({ sampleMeasurements: updated })
    setClickedPoints([])
    setManualDrawingIndex(null)
    setIsConfirmModalOpen(false)
    pendingManualRef.current = null
  }, [manualDrawingIndex, rowSample, updateRowSample, setManualDrawingIndex])

  const onClickModalCancel = useCallback(() => {
    handleCancel()
    setIsConfirmModalOpen(false)
    pendingManualRef.current = null
  }, [handleCancel])

  // 外部クリックで測定キャンセル
  useEffect(() => {
    if (manualDrawingIndex === null) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        canvasWrapperRef.current &&
        !canvasWrapperRef.current.contains(e.target as Node)
      ) {
        handleCancel()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [manualDrawingIndex, handleCancel])

  return (
    <div className="bg-white w-full h-[615px] rounded-md desktop-xmd:max-w-[1000px] desktop-xmd:min-w-[600px]" ref={canvasWrapperRef}>
      <div className="flex justify-between items-center pl-5 pr-5 h-[46px] bg-[#F3F3F3] border-b-black border-[1px]">
        <div className="flex items-center border-l-black border-l-8 pl-[5px] h-[38px] text-[18px]">{t('pages.shootingImage')}</div>
        <button className="flex items-center justify-center bg-black h-[34px] w-[34px] rounded-[5px]">
          <img src={resizeIcon} alt='' className='w-[19px]' />
        </button>
      </div>
      <div
        ref={containerRef}
        className='relative h-[calc(100%-46px)] flex items-center justify-center overflow-hidden'
      >
        <div
          className='relative'
          style={
            canvasSize.w && canvasSize.h
              ? {
                width: `${canvasSize.w}px`,
                height: `${canvasSize.h}px`,
                cursor:
                  isDraggingPoint
                    ? 'none'
                    : zoom !== 1 && (isSpacePressed || isPanning)
                    ? (isPanning ? 'grabbing' : 'grab')
                    : 'default',
              }
              : {}
          }
          onMouseDown={handlePanMouseDown}
          onTouchStart={handlePanTouchStart}
          onTouchMove={handlePanTouchMove}
          onTouchEnd={handlePanTouchEnd}
          onTouchCancel={handlePanTouchEnd}
        >
          <div
            className='relative'
            style={
              canvasSize.w && canvasSize.h
                ? {
                  width: `${canvasSize.w}px`,
                  height: `${canvasSize.h}px`,
                  transform: `translate(${pan.x}px, ${pan.y}px)`
                }
                : {}
            }
          >
            <div
              className='relative'
              style={
                canvasSize.w && canvasSize.h
                  ? {
                    width: `${canvasSize.w}px`,
                    height: `${canvasSize.h}px`,
                    transform: `scale(${zoom})`,
                    transformOrigin: 'center center'
                  }
                  : {}
              }
            >
              <canvas
                ref={canvasRef}
                onClick={handleCanvasClick}
                onMouseMove={e => {
                  if (
                    manualDrawingIndex !== null &&
                    clickedPoints.length >= 1 &&
                    draggingIndex === null &&
                    interactionMode === 'draw'
                  ) {
                    if (isSpacePressed || isPanning) {
                      setPreviewPoint(null)
                      return
                    }
                    const relative = getCanvasRelativePoint(e.clientX, e.clientY)
                    if (!relative) return
                    setPreviewPoint(relative)
                  }
                }}
              />
              <div
                className='absolute top-0 left-0'
                style={{
                  width: canvasSize.w,
                  height: canvasSize.h,
                  pointerEvents: 'none',
                }}
              >
                {clickedPoints.map((pt, idx) => (
                  <Draggable
                    key={idx}
                    nodeRef={nodeRef}
                    defaultPosition={{ x: pt.x, y: pt.y }}
                    bounds="parent"
                    positionOffset={{ x: -TRIANGLE_HALF_WIDTH, y: -TRIANGLE_HEIGHT }}
                    disabled={interactionMode !== 'move' || isSpacePressed || isPanning}
                    scale={zoom}
                    onStart={() => {
                      if (interactionMode !== 'move' || isSpacePressed || isPanning) return false
                      setDraggingIndex(idx)
                      return undefined
                    }}
                    onDrag={(_e: DraggableEvent, data: DraggableData) => {
                      if (interactionMode !== 'move' || isSpacePressed || isPanning) {
                        return false
                      }
                      setClickedPoints(prev =>
                        prev.map((p, i) => (i === idx ? { x: data.x, y: data.y } : p))
                      )
                      setDraggingPos({ x: data.x, y: data.y })
                    }}
                    onStop={() => {
                      setDraggingIndex(null)
                    }}
                  >
                    <div
                      ref={nodeRef}
                      style={{
                        width: 0,
                        height: 0,
                        left: 0,
                        top: 0,
                        position: 'absolute',
                        cursor:
                          interactionMode === 'move' && !isSpacePressed && !isPanning && draggingIndex === idx
                            ? 'none'
                            : 'default',
                        pointerEvents:
                          interactionMode === 'move' && !isSpacePressed && !isPanning ? 'all' : 'none',
                        zIndex: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 0,
                          height: 0,
                          left: -TRIANGLE_OUTLINE_OFFSET,
                          top: -TRIANGLE_OUTLINE_OFFSET,
                          borderLeft: `${TRIANGLE_HALF_WIDTH + TRIANGLE_OUTLINE_EXTRA_WIDTH}px solid transparent`,
                          borderRight: `${TRIANGLE_HALF_WIDTH + TRIANGLE_OUTLINE_EXTRA_WIDTH}px solid transparent`,
                          borderTop: `${TRIANGLE_HEIGHT + TRIANGLE_OUTLINE_EXTRA_HEIGHT}px solid ${TRIANGLE_OUTLINE_COLOR}`,
                          position: 'absolute',
                        }}
                      />
                      <div
                        style={{
                          width: 0,
                          height: 0,
                          left: 0,
                          top: -TRIANGLE_INNER_TOP_OFFSET,
                          borderLeft: `${TRIANGLE_HALF_WIDTH}px solid transparent`,
                          borderRight: `${TRIANGLE_HALF_WIDTH}px solid transparent`,
                          borderTop: `${TRIANGLE_HEIGHT}px solid ${getColor(manualDrawingIndex!)}`,
                          position: 'absolute',
                        }}
                      />
                    </div>
                  </Draggable>
                ))}
              </div>
            </div>
          </div>
        </div>
        {(!isCanvasReady && hasImage) || isBaseImageLoading ? (
          <div className='absolute inset-0 flex items-center justify-center bg-white/70 z-20'>
            <ReactLoading type="spin" color="#545899" width={64} height={64} />
          </div>
        ) : null}
        {manualDrawingIndex !== null && (
          <div className='absolute flex h-[30px] space-x-2.5 bottom-[5px] right-[10px]'>
            <button
              onClick={handleComplete}
              className='w-[100px] bg-[#C5C7FF] rounded-[50px] text-[#545899] font-bold'
            >
              {t('common.done')}
            </button>
            <button
              onClick={handleCancel}
              className='w-[100px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] font-bold'
            >
              {t('common.cancel')}
            </button>
          </div>
        )}
        {manualDrawingIndex !== null && (
          <div className='absolute flex flex-col h-[30px] space-y-2.5 top-2.5 right-2.5'>
            <button
              onClick={() => setInteractionMode('draw')}
              className={`flex justify-center items-center space-x-[5px] py-1 w-[140px] rounded-[50px] font-bold ${interactionMode === 'draw'
                ? 'bg-[#C5C7FF] text-[#545899]'
                : 'border-[1.5px] border-[#C5C7FF] text-[#545899]'
                }`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0 text-blue-600">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="whitespace-nowrap text-[14px]">{t('drawing.mode')}</span>
            </button>
            <button
              onClick={() => setInteractionMode('move')}
              className={`flex justify-center items-center space-x-[5px] py-1 w-[140px] rounded-[50px] font-bold ${interactionMode === 'move'
                ? 'bg-[#C5C7FF] text-[#545899]'
                : 'border-[1.5px] border-[#C5C7FF] text-[#545899]'
                }`}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0 text-blue-600">
                <path d="M12 3V21M12 3L9 6M12 3L15 6M12 21L9 18M12 21L15 18M3 12H21M3 12L6 9M3 12L6 15M21 12L18 9M21 12L18 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="whitespace-nowrap text-[14px]">{t('drawing.moveMode')}</span>
            </button>
          </div>
        )}
        {manualDrawingIndex !== null && (
          <div className='absolute bottom-0 left-0 m-2 max-w-[350px] text-xs bg-black/60 p-2 rounded-md text-white'>
            <ul className="mt-1 space-y-0.5 leading-snug">
              <li>
                <span className="font-semibold">{t('drawing.moveHelpLabel')}</span> {t('drawing.moveHelp')}
              </li>
              <li>
                <span className="font-semibold">{t('drawing.zoomHelpLabel')}</span> {t('drawing.zoomHelp', { modifier: isMac ? "⌥ Option" : "Ctrl" })}
              </li>
            </ul>
          </div>
        )}
      </div>
      <Modal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        className="flex flex-col items-center justify-center min-w-[414px] h-[189px] px-[25px]"
      >
        <span className='text-center'>
          <p>{t('messages.unnamedMeasurementExists')}</p>
          <p>{t('messages.overwriteConfirm')}</p>
        </span>
        <div className='space-x-2 mt-[15px]'>
          <button
            onClick={onClickModalCancel}
            className="w-40 h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onClickModalOk}
            className="w-40 h-[47px] bg-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  )
}
